import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { subscribeMaintenance, resetFailureTracking } from '../utils/maintenanceBus';

// Story 73 — full-screen maintenance gate, tripped by the axios interceptor
// (see maintenanceBus.js) on backend-unreachable. Deliberately NOT gated on
// auth state: an outage during login is just as broken as one on the
// dashboard, so this wraps the whole app, above AuthProvider, in App.js.
const HEALTH_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/actuator/health`;
const POLL_INTERVAL_MS = 15000;
const HEALTH_TIMEOUT_MS = 5000;

const MaintenanceContext = createContext(null);

export function MaintenanceProvider({ children }) {
  const [isMaintenance, setIsMaintenance] = useState(false);
  const [checking, setChecking] = useState(false);
  const intervalRef = useRef(null);

  const checkHealth = useCallback(async () => {
    setChecking(true);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), HEALTH_TIMEOUT_MS);
      const res = await fetch(HEALTH_URL, { signal: controller.signal });
      clearTimeout(timeoutId);
      if (res.ok) {
        resetFailureTracking();
        setIsMaintenance(false);
      }
    } catch {
      // Still down (or the health check itself failed) — stay in maintenance.
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => subscribeMaintenance(() => setIsMaintenance(true)), []);

  useEffect(() => {
    if (!isMaintenance) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return undefined;
    }

    checkHealth();
    intervalRef.current = setInterval(checkHealth, POLL_INTERVAL_MS);
    return () => {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [isMaintenance, checkHealth]);

  const retryNow = useCallback(() => {
    checkHealth();
  }, [checkHealth]);

  const value = { isMaintenance, checking, retryNow };

  return (
    <MaintenanceContext.Provider value={value}>
      {children}
    </MaintenanceContext.Provider>
  );
}

export function useMaintenance() {
  const ctx = useContext(MaintenanceContext);
  if (!ctx) throw new Error('useMaintenance must be used within MaintenanceProvider');
  return ctx;
}
